import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { Spinner } from '../../../ui/Spinner';
import { btnPrimary, btnQuiet, inputCls } from '../../../ui/styles';
import { FormError } from '../../shared/FormError';

/**
 * AVG-rechten in Gegevens: alles downloaden (art. 15/20) en het account verwijderen (art. 17).
 * Verwijderen in twee stappen, met het e-mailadres ter bevestiging; de server vraagt daarnaast
 * een recente login.
 */
export function DataRights() {
  const {
    account,
    downloadData,
    deleteAccount,
    signOut
  } = useSignedInAccount();
  const [downloading, setDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [email, setEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 423 `reauth_required`: dezelfde uitweg als bij het wachtwoord, opnieuw inloggen. */
  const [reauth, setReauth] = useState(false);
  /** 409 `open_balance`: nog tegoed of een lopende kuur; eerst met de salon afhandelen. */
  const [openBalance, setOpenBalance] = useState(false);
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const res = await downloadData();
    setDownloading(false);
    if (res.ok) toast.success('Je gegevens staan in je downloads');else toast.error('Downloaden lukte niet', {
      description: res.error
    });
  };
  const remove = async (e: FormEvent) => {
    e.preventDefault();
    if (deleting) return;
    if (email.trim().toLowerCase() !== account.customer.email.toLowerCase()) {
      setError('Typ het e-mailadres van je account, precies zoals hierboven.');
      return;
    }
    setDeleting(true);
    setError(null);
    const res = await deleteAccount(email);
    setDeleting(false);
    if (res.ok) toast.success('Je account is verwijderd', {
      description: 'Bedankt voor het vertrouwen. Je bent altijd welkom terug.'
    });else {
      setReauth(res.code === 'reauth_required');
      setOpenBalance(res.code === 'open_balance');
      setError(res.error);
    }
  };
  return <section id="privacy" aria-labelledby="data-rights-title" className="mt-10 scroll-mt-24" data-id="element-6531" data-mp="35fx5kb3yirx5">
      <h2 id="data-rights-title" className="font-display text-[24px] font-light text-ink" data-id="element-6532" data-mp="3t619kjl24too">
        Privacy en je gegevens
      </h2>
      <div className="mt-4 divide-y divide-line border-y border-line" data-id="element-6533" data-mp="10xewv6mp5bm">
        <div className="flex flex-wrap items-center justify-between gap-3 py-4" data-id="element-6534" data-mp="3jg81vto1cypp">
          <div className="min-w-0" data-id="element-6535" data-mp="3of2rbegidlis">
            <p className="text-[15px] text-ink" data-id="element-6536" data-mp="3oe9kkdhjuw0n">Download alles wat we van je bewaren</p>
            <p className="mt-0.5 text-[13px] text-muted" data-id="element-6537" data-mp="fyputog37vqc">Gegevens, bestellingen, afspraken, huidplan en dagboek, in één bestand.</p>
          </div>
          <button type="button" onClick={() => void download()} disabled={downloading} className={btnQuiet} data-id="element-6538" data-mp="krajkbk4zin0">
            {downloading ? <Spinner data-id="element-6539" data-mp="q36767ucyupe" /> : <DownloadIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6540" data-mp="q3fvlqzr8tsz" />}
            {downloading ? 'Even geduld…' : 'Download'}
          </button>
        </div>

        <div className="py-4" data-id="element-6541" data-mp="13wfcnxf6qw2n">
          {confirming ? <form onSubmit={e => void remove(e)} noValidate data-id="element-6542" data-mp="ywsztp39rhud">
              <p className="text-[15px] text-ink" data-id="element-6543" data-mp="b5ni14drqfhf">Account verwijderen</p>
              <p className="mt-1 max-w-prose text-[14px] leading-relaxed text-muted" data-id="element-6544" data-mp="b7qkinw3y131">
                Je gegevens, huidplan, afspraken en foto’s worden verwijderd. Loopt er een abonnement, dan stopt het. Bestellingen bewaren we zeven jaar voor de
                belastingdienst, zonder koppeling aan je account. Dit kun je niet terugdraaien.
              </p>
              <label htmlFor={fieldId} className="mt-4 block text-[14px] text-ink" data-id="element-6545" data-mp="1whyqmmr6a9lb">
                Typ ter bevestiging je e-mailadres <span className="text-muted" data-id="element-6546" data-mp="1rjxaa9l4sodj">({account.customer.email})</span>
              </label>
              <input id={fieldId} type="email" autoComplete="off" value={email} onChange={e => setEmail(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} className={`${inputCls} mt-1.5 max-w-sm`} data-id="element-6547" data-mp="14w9wh33w8ur" />
              {reauth && error ? <div id={errorId} role="alert" className="mt-3 max-w-prose border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-6548" data-mp="k0359jqua39x">
                  <p data-id="element-6549" data-mp="tt1v14y2nzv4">{error}</p>
                  <button type="button" onClick={signOut} className={`${btnPrimary} mt-3`} data-id="element-6550" data-mp="tttqmfro5xle">
                    Opnieuw inloggen
                  </button>
                </div> : openBalance && error ? <div id={errorId} role="alert" className="mt-3 max-w-prose border border-clay/40 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-6551" data-mp="17ri595v588i0">
                  <p data-id="element-6552" data-mp="2sb3zzkgbsald">{error}</p>
                  <Link to="/contact" className={`${btnPrimary} mt-3`} data-id="element-6553" data-mp="2s76hn11knn2j">
                    Contact met de salon
                  </Link>
                </div> : <FormError id={errorId} message={error} className="mt-3" data-id="element-6554" data-mp="2j94fuja0kqe7" />}
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-6555" data-mp="2ebm6wsvzyb67">
                <button type="submit" disabled={deleting} className={btnPrimary} data-id="element-6556" data-mp="3b0aawlavnxwm">
                  {deleting && <Spinner data-id="element-6557" data-mp="36xx8o6vcai4l" />}
                  {deleting ? 'Bezig met verwijderen…' : 'Verwijder mijn account'}
                </button>
                <button type="button" onClick={() => {
              setConfirming(false);
              setEmail('');
              setError(null);
              setReauth(false);
              setOpenBalance(false);
            }} className={btnQuiet} data-id="element-6558" data-mp="3265qokq91kq2">
                  Terug
                </button>
              </div>
            </form> : <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-6559" data-mp="hnrzokmi0qcg">
              <div className="min-w-0" data-id="element-6560" data-mp="3vtyqx2xy0rd">
                <p className="text-[15px] text-ink" data-id="element-6561" data-mp="3uskkjqmoy5f">Account verwijderen</p>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-6562" data-mp="7wxcne1fhmpy">Je krijgt eerst te zien wat er verdwijnt.</p>
              </div>
              <button type="button" onClick={() => setConfirming(true)} className={`${btnQuiet} text-muted`} data-id="element-6563" data-mp="3q2aer6aux65k">
                Verwijderen
              </button>
            </div>}
        </div>
      </div>
    </section>;
}